import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { logo } from "../../assets/images";
import { motion } from "framer-motion";
import { CircleUser, Menu, Bell } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { fetchNotifications, markNotificationAsRead } from "../../store/reducers/slices/notificationSlice";
import { Link } from "react-router-dom";

export default function Header({
  setOpen,
}: {
  setOpen: (val: boolean) => void;
}) {
  const [openNotification, setOpenNotification] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // const [selectedNotification, setSelectedNotification] =
  //   useState<any>(null);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const unreadCount = useAppSelector(
    (state) => state.notification.unreadCount
  );

  const notifications = useAppSelector(
    (state) => state.notification.notifications
  );

  // ✅ SORT ONLY WHEN DATA CHANGES
const latestNotifications = useMemo(() => {
  return notifications
    .filter((n) => !n.is_read) // only unread
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() -
        new Date(a.created_at).getTime()
    )
    .slice(0, 5);
}, [notifications]);

  const [selectedNotificationId, setSelectedNotificationId] =
    useState<string | null>(null);

  const selectedNotification = notifications.find(
    (n) => n.id === selectedNotificationId
  );

  // ✅ STABLE HOVER HANDLERS
  const handleEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenNotification(true);
  }, []);

  const handleLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setOpenNotification(false);
    }, 200);
  }, []);


  // ✅ FETCH ONCE
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  const handleSelectNotification = useCallback(
    (notification: any) => {
      setSelectedNotificationId(notification.id);

      if (!notification.is_read) {
        dispatch(markNotificationAsRead(notification.id));
      }
    },
    [dispatch]
  );

  return (
    <motion.header
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-md px-6 py-3 flex justify-between items-center border-b border-white/5 text-white"
    >
      {/* LEFT */}
      <div className="flex items-center gap-3">
        <span
          className="md:hidden cursor-pointer"
          onClick={() => setOpen(true)}
        >
          <Menu className="w-6 h-6" />
        </span>

        <img src={logo} alt="" className="w-20 h-10" />
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-4">
        {/* NOTIFICATION */}
        <div
          className="relative"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <Bell className="w-5 h-5 text-gray-300 hover:text-white cursor-pointer" />

          {/* BADGE */}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] px-1.5 rounded-full">
              {unreadCount}
            </span>
          )}

          {/* DROPDOWN */}
          {openNotification && (
            <div className="absolute right-0 mt-3 w-80 bg-white text-black rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="p-3 border-b font-semibold">
                Notifications
              </div>

              {latestNotifications.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">
                  No notifications
                </div>
              ) : (
                latestNotifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => handleSelectNotification(n)}
                    className={`p-3 border-b cursor-pointer transition hover:bg-gray-100 ${n.is_read
                      ? "bg-blue-50 border-l-4 border-l-blue-500"
                      : "opacity-70"
                      }`}
                  >
                    <p className="font-medium text-sm">
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-600">
                      {n.message}
                    </p>
                    {n.data?.property && (
                      <div className="flex justify-between items-center text-xs text-gray-600 mt-1 space-y-1">
                        <div>
                          <p>{n.data.property.title}</p>
                          <p>{n.data.property.address}</p>
                        </div>
                        <div>
                          <p className="font-semibold">
                            ₦{Number(n.data.property.price).toLocaleString()}
                          </p>
                        </div>


                      </div>
                    )}
                  </div>
                ))
              )}

              <Link
                to="/user/notifications"
                className="block p-2 text-center text-xs text-blue-500 hover:underline"
              >
                View all
              </Link>
            </div>
          )}
        </div>

        {/* USER */}
        <div className="cursor-pointer">
          {user?.image_url ? (
            <img
              src={user.image_url}
              alt="user"
              className="w-9 h-9 rounded-full object-cover border"
            />
          ) : (
            <CircleUser className="w-8 h-8" />
          )}
        </div>
      </div>

      {selectedNotification && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3">

          {/* CENTER WRAPPER */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white text-black w-full max-w-md rounded-xl shadow-2xl overflow-hidden"
          >

            {/* HEADER */}
            <div className="flex justify-between items-center border-b p-4">
              <h2 className="font-semibold text-base md:text-lg">
                Notification Details
              </h2>

              <button
                onClick={() => setSelectedNotificationId(null)}
                className="text-xl md:text-sm text-gray-500 hover:text-black p-2"
              >
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div className="p-4 space-y-4 max-h-[65vh] overflow-y-auto">

              {/* TITLE */}
              <div>
                <p className="text-[10px] text-gray-500">Title</p>
                <h3 className="font-semibold text-base md:text-lg break-words">
                  {selectedNotification.title}
                </h3>
              </div>

              {/* MESSAGE */}
              <div>
                <p className="text-[10px] text-gray-500">Message</p>
                <p className="text-sm text-gray-700 break-words">
                  {selectedNotification.message}
                </p>
              </div>

              {/* TYPE + STATUS (STACK ON MOBILE) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div>
                  <p className="text-[10px] text-gray-500">Type</p>
                  <p className="font-medium text-sm">
                    {selectedNotification.type}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] text-gray-500">Status</p>
                  <span
                    className={`text-sm font-medium ${selectedNotification.is_read
                        ? "text-green-600"
                        : "text-red-500"
                      }`}
                  >
                    {selectedNotification.is_read ? "Read" : "Unread"}
                  </span>
                </div>
              </div>

              {/* DATE */}
              <div>
                <p className="text-[10px] text-gray-500">Date</p>
                <p className="text-sm">
                  {new Date(selectedNotification.created_at).toLocaleString()}
                </p>
              </div>

              {/* PROPERTY CARD (MOBILE FRIENDLY) */}
              {selectedNotification.data?.property && (
                <div className="bg-zinc-100 rounded-lg p-3 space-y-1">

                  <p className="text-sm font-semibold break-words">
                    Property Name:  {selectedNotification.data.property.title}
                  </p>

                  <p className="text-xs text-gray-600 break-words">
                    Property Address: {selectedNotification.data.property.address}
                  </p>

                  <p className="text-sm font-bold text-black">
                    Property Price: ₦{Number(selectedNotification.data.property.price).toLocaleString()}
                  </p>

                </div>
              )}

            </div>

            {/* FOOTER */}
            <div className="border-t p-3 flex justify-end">
              <button
                onClick={() => setSelectedNotificationId(null)}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm"
              >
                Close
              </button>
            </div>

          </motion.div>
        </div>
      )}
    </motion.header>
  );
}